import { CommonModule, NgClass } from '@angular/common';
import { Component, Type, computed, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BaseDataGridCellRenderer } from 'shared/abstract/base-data-grid-cell-renderer';
import { SimpleTooltipComponent } from 'shared/components/simple-tooltip/simple-tooltip.component';
import { TooltipDirective } from 'shared/directives/tooltip.directive';
import { ColoredPillTypesEnum } from 'shared/enums/colored-pill-types.enum';
import { CustomColoredPillTypesEnum } from 'shared/enums/custom-colored-pill-types.enum';

export interface PillCellRendererParams {
    pillType: Function;
    shouldDisplay?: Function;
    valueFormat?: Function;
    multiPillsDisplayMapping?: { [key: string]: ColoredPillTypesEnum | CustomColoredPillTypesEnum };
    isMultiPillDisplay?: boolean;
    tooltip?: {
        text?: string | Function;
        component?: Type<any>;
        componentData?: any | Function;
        position?: 'top' | 'bottom' | 'left' | 'right';
        trigger?: 'click' | 'hover';
    };
}

@Component({
    selector: 'app-data-grid-colored-pill-cell-renderer',
    templateUrl: './data-grid-colored-pill-cell-renderer.component.html',
    styleUrl: './data-grid-colored-pill-cell-renderer.component.scss',
    imports: [TranslateModule, NgClass, CommonModule, TooltipDirective],
})
export class DataGridColoredPillCellRendererComponent extends BaseDataGridCellRenderer {
    protected pillType = signal<ColoredPillTypesEnum | CustomColoredPillTypesEnum>(ColoredPillTypesEnum.INFO);
    protected displayValue = signal<string>('');
    protected shouldDisplayPill = signal<boolean>(true);

    protected isMultiPillDisplay = signal<boolean>(false);
    protected multiPillsDisplayMapping = signal<{ [key: string]: ColoredPillTypesEnum | CustomColoredPillTypesEnum }>({});
    protected valuesArray = signal<string[]>([]);

    protected hasTooltip = signal<boolean>(false);
    protected tooltipComponent = signal<Type<any>>(SimpleTooltipComponent);
    protected tooltipData = signal<any>({});
    protected tooltipPosition = signal<'top' | 'bottom' | 'left' | 'right'>('right');
    protected tooltipTrigger = signal<'click' | 'hover'>('hover');

    protected cellWrapperClass = computed(() => (!this.isMultiPillDisplay() ? 'centered' : ''));

    public onDataChange(): void {
        if (!this.column || !this.data || !this.column.dataKey) {
            return;
        }

        const rendererParams: PillCellRendererParams = this.column.customCellRendererData;
        const data = this.data as any;
        const cellValue = data[this.column.dataKey];

        const isMulti = rendererParams.isMultiPillDisplay ?? false;
        this.isMultiPillDisplay.set(isMulti);

        if (!isMulti) {
            this.pillType.set(rendererParams.pillType.call(this, String(cellValue)) as ColoredPillTypesEnum | CustomColoredPillTypesEnum);

            this.displayValue.set(rendererParams.valueFormat ? rendererParams.valueFormat.call(this, cellValue) : cellValue);
        } else {
            this.multiPillsDisplayMapping.set(rendererParams.multiPillsDisplayMapping ?? {});
            this.valuesArray.set(cellValue?.split(',').map((value: string) => value.trim()) ?? []);
        }

        this.shouldDisplayPill.set(rendererParams.shouldDisplay ? rendererParams.shouldDisplay.call(this, cellValue) : true);

        this.setupTooltip(rendererParams, cellValue);
    }

    private setupTooltip(rendererParams: PillCellRendererParams, cellValue: any): void {
        const tooltipConfig = rendererParams.tooltip;

        if (!tooltipConfig) {
            this.hasTooltip.set(false);
            return;
        }

        this.hasTooltip.set(true);
        this.tooltipPosition.set(tooltipConfig.position ?? 'right');
        this.tooltipTrigger.set(tooltipConfig.trigger ?? 'hover');

        if (tooltipConfig.component) {
            this.tooltipComponent.set(tooltipConfig.component);

            if (tooltipConfig.componentData) {
                this.tooltipData.set(
                    typeof tooltipConfig.componentData === 'function'
                        ? tooltipConfig.componentData.call(this, cellValue)
                        : tooltipConfig.componentData,
                );
            }
        } else if (tooltipConfig.text) {
            this.tooltipComponent.set(SimpleTooltipComponent);
            this.tooltipData.set(typeof tooltipConfig.text === 'function' ? tooltipConfig.text.call(this, cellValue) : tooltipConfig.text);
        }
    }

    refresh(): boolean {
        return false;
    }
}
